import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Capacitor } from '@capacitor/core';
import { useTheme } from '../context/ThemeContext';
import { useProfile } from '../context/ProfileContext';

// ── Abgerundete Tastatur-Kappe (NUR nativ) ──────────────────────────────────
// Ersetzt die graue, gerade iOS-Accessory-Bar (die der User nicht mochte) durch
// eine app-farbene Leiste mit runden oberen Ecken + „Fertig". Sitzt exakt auf der
// Tastatur (bottom:0 + translateY(-Tastaturhöhe)) und reitet per --ease-kb
// synchron mit der iOS-Tastatur hoch/runter. Im Web rendert sie nichts
// (Capacitor.isNativePlatform() === false) → keine Web-Verhaltensänderung.
// Höhe (44) ist im capacitorBootstrap als Teil von --kbpad reserviert, damit
// Sheet-Listen/Popups NICHT hinter der Kappe verschwinden.
export default function KeyboardCap() {
  const { T } = useTheme();
  const { language } = useProfile();
  const [kbH, setKbH] = useState(0);
  const native = typeof Capacitor !== 'undefined' && Capacitor.isNativePlatform?.();

  useEffect(() => {
    if (!native) return undefined;
    let mounted = true;
    let showSub;
    let hideSub;
    (async () => {
      try {
        const { Keyboard } = await import('@capacitor/keyboard');
        showSub = await Keyboard.addListener('keyboardWillShow', (info) => {
          if (mounted) setKbH((info && info.keyboardHeight) || 300);
        });
        hideSub = await Keyboard.addListener('keyboardWillHide', () => {
          if (mounted) setKbH(0);
        });
      } catch (_) { /* Keyboard-Plugin optional */ }
    })();
    return () => {
      mounted = false;
      try { showSub && showSub.remove && showSub.remove(); } catch (_) {}
      try { hideSub && hideSub.remove && hideSub.remove(); } catch (_) {}
    };
  }, [native]);

  if (!native) return null;

  const visible = kbH > 0;
  const dismiss = async () => {
    try { const { Keyboard } = await import('@capacitor/keyboard'); await Keyboard.hide(); } catch (_) {}
  };

  return createPortal(
    <div
      aria-hidden={!visible}
      style={{
        position: 'fixed', left: 0, right: 0, bottom: 0, height: 44, zIndex: 4000,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 16px',
        background: T.surface,
        borderTop: `1px solid ${T.line}`,
        borderRadius: '16px 16px 0 0',
        boxShadow: '0 -8px 22px rgba(0,0,0,0.28)',
        // sitzt auf der Tastatur, wenn sichtbar; sonst um die eigene Höhe nach unten weggeschoben
        transform: visible ? `translateY(${-kbH}px)` : 'translateY(48px)',
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? 'auto' : 'none',
        transition: 'transform var(--dur-kb) var(--ease-kb), opacity var(--dur-kb) var(--ease-kb)',
      }}
    >
      <span style={{ fontSize: 12.5, fontWeight: 800, letterSpacing: '-0.02em', color: T.mute }}>
        red<span style={{ color: T.red || '#E4544A' }}>.</span>
      </span>
      <button
        onMouseDown={(e) => e.preventDefault()}
        onClick={dismiss}
        style={{
          border: 'none', background: 'none', color: T.red || '#E4544A',
          fontFamily: 'inherit', fontWeight: 700, fontSize: 15, padding: '8px 4px',
          cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
        }}
      >
        {language === 'de' ? 'Fertig' : 'Done'}
      </button>
    </div>,
    document.body
  );
}
